import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { B, FONT, fmtDate } from '../lib/constants';

export default function NeededPurchases({ projectId, onOpenPurchases }) {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [projectId]);

  const load = async () => {
    const { data } = await supabase
      .from('purchases')
      .select('*')
      .eq('project_id', projectId)
      .neq('status', 'on_site')
      .order('needed_by', { ascending: true, nullsLast: true })
      .order('created_at');
    if (data) setPurchases(data);
    setLoading(false);
  };

  const handleToggle = async (p) => {
    await supabase.from('purchases').update({
      client_purchased: !p.client_purchased,
      status: !p.client_purchased ? 'ordered' : 'needed'
    }).eq('id', p.id);
    load();
  };

  if (loading || purchases.length === 0) return null;

  const clientItems = purchases.filter(p => p.purchased_by === 'client');
  const mullerItems = purchases.filter(p => p.purchased_by === 'muller');

  return (
    <div style={{ background: B.navy, borderRadius: 14, padding: 18, marginBottom: 12, border: `1px solid ${B.gold}`, fontFamily: FONT }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', fontWeight: 700 }}>
          📦 Purchases Needed
        </div>
        <button onClick={onOpenPurchases}
          style={{ background: 'transparent', border: 'none', color: B.goldlt, fontSize: 12, cursor: 'pointer', fontFamily: FONT, fontWeight: 700 }}>
          See All ›
        </button>
      </div>

      {clientItems.length > 0 && (
        <div style={{ marginBottom: mullerItems.length > 0 ? 14 : 0 }}>
          <div style={{ color: B.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>You Purchase</div>
          {clientItems.map(p => (
            <PurchaseRow key={p.id} purchase={p} onToggle={() => handleToggle(p)} />
          ))}
        </div>
      )}

      {mullerItems.length > 0 && (
        <div>
          <div style={{ color: B.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Muller Purchases</div>
          {mullerItems.map(p => (
            <PurchaseRow key={p.id} purchase={p} readOnly />
          ))}
        </div>
      )}
    </div>
  );
}

function PurchaseRow({ purchase: p, onToggle, readOnly }) {
  const today = new Date(); today.setHours(0,0,0,0);
  const isUrgent = p.needed_by && Math.round((new Date(p.needed_by + 'T12:00:00') - today) / 86400000) <= 7;
  const isPurchased = p.client_purchased || p.status === 'ordered';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 10, marginBottom: 10, borderBottom: `1px solid ${B.slate}` }}>
      {!readOnly ? (
        <div onClick={onToggle}
          style={{ width: 24, height: 24, borderRadius: 7, border: `2px solid ${isPurchased ? '#4CAF7D' : B.gold}`, background: isPurchased ? '#4CAF7D' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}>
          {isPurchased && <span style={{ color: '#fff', fontSize: 14, fontWeight: 800 }}>✓</span>}
        </div>
      ) : (
        <div style={{ width: 24, height: 24, borderRadius: 7, border: `2px solid ${B.slate}`, background: B.slate, flexShrink: 0 }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: isPurchased ? B.muted : B.white, fontSize: 13, fontWeight: 600, textDecoration: isPurchased ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.item}{p.room ? ` — ${p.room}` : ''}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
          <div style={{ color: B.muted, fontSize: 11 }}>{p.category}</div>
          {p.needed_by && !isPurchased && (
            <div style={{ color: isUrgent ? '#E74C3C' : B.muted, fontSize: 11, fontWeight: isUrgent ? 700 : 400 }}>
              {isUrgent ? '⚠️ ' : ''}by {fmtDate(p.needed_by)}
            </div>
          )}
          {p.url && (
            <div onClick={e => { e.stopPropagation(); window.open(p.url, '_blank'); }}
              style={{ color: B.goldlt, fontSize: 11, textDecoration: 'underline', cursor: 'pointer' }}>
              🔗 Link
            </div>
          )}
        </div>
      </div>
      {!readOnly && !isPurchased && (
        <div style={{ background: 'rgba(248,218,107,0.1)', border: `1px solid ${B.gold}`, borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700, color: B.goldlt, flexShrink: 0 }}>
          Needed
        </div>
      )}
      {isPurchased && (
        <div style={{ background: 'rgba(76,175,61,0.1)', border: '1px solid #4CAF7D', borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700, color: '#4CAF7D', flexShrink: 0 }}>
          Ordered
        </div>
      )}
    </div>
  );
}
